// src/utils/failureDisplay.ts
//
// Pure presentation logic for the Settings → Failures view (Story 4.8). The view
// unifies FIVE failure categories drawn from FOUR sources into one newest-first
// table (Decision 10 — capped client-side reads, merged here; a SQL UNION
// view is premature at pilot volume):
//   (a) dead letters incl. bounces — all yak_dead_letters kinds
//   (b) outbox delivery failures   — yak_notifications channel IN ('email','sms')
//                                     status='failed' (6.1 added the sms transport)
//   (c) unrecognized-caller attempts — yak_calls disposition='unrecognized_caller'
//   (d) caller-verification lockouts — yak_call_verifications status='locked_out'
//                                     (Story 6.2, FR-42)
//
// mergeFailureRows normalizes the four shapes into one FailureRow, sorts them
// newest-first (id tiebreak for stability), and carries everything the component
// renders. It is pure — the component and hook never re-derive attribution or
// re-query (compose-from-rows, 3.7's posture). Chip tones follow DESIGN.md:
// bounce + verification lockout = orange (advisory), dead-letter kinds + outbox
// failures = red (critical), unrecognized-caller = neutral gray (an
// adoption/config signal, never an error).

import type {
  YakCall,
  YakCallVerification,
  YakDeadLetter,
  YakDeadLetterKind,
  YakNotification,
  YakOutboxChannel,
} from '../types/yak'

// The view paginates client-side at 25/page (never infinite scroll).
export const FAILURES_PAGE_SIZE = 25

// Each feed is read newest-first and capped (Decision 10 — the honesty valve is
// the cap + a "showing the most recent" note when a feed hits it).
export const FAILURES_FETCH_LIMIT = 200

// The unified failure-row category. 'bounce' is split out from the other
// dead-letter kinds because its chip tone (orange advisory) and its rep-record
// link differ; 'outbox' and 'unrecognized_caller' are the non-dead-letter feeds.
export type FailureCategory =
  | 'dead_letter'
  | 'bounce'
  | 'outbox'
  | 'unrecognized_caller'
  // Story 6.2: a caller who failed the one-time-code read-back three times on
  // one call. Advisory, not critical — it is usually a rep with a stale number
  // on the roster, and the remediation is the rep record.
  | 'verification'

export type FailureChipTone = 'orange' | 'red' | 'gray'

// One normalized row over the three sources. `detail` is the read-only raw
// expander payload (the whitelisted dead-letter payload, or the outbox
// last_error/attempts, or the call number/disposition). `repId` +
// `repEmailBouncedAt` drive the bounce row's rep link + live flag line;
// `ticketId` deep-links the ticket when set.
export interface FailureRow {
  id: string
  source: 'dead_letter' | 'notification' | 'call' | 'verification'
  category: FailureCategory
  // The dead-letter kind for dead_letter/bounce rows (drives the label); null
  // for outbox/unrecognized-caller rows.
  kind: YakDeadLetterKind | null
  // The outbox transport for notification rows; null for the other two feeds.
  // Category stays 'outbox' for both — only the wording differs (Story 6.1).
  // Narrowed to the outbox transports so failureLabel's `=== 'sms' ? … : Email`
  // cannot mislabel an in_app row (Story 6.1 review).
  channel: YakOutboxChannel | null
  occurredAt: string
  // A one-line human summary (kind label + attribution hint).
  summary: string
  // Attribution hints for the table's middle column.
  organizationId: string | null
  ticketId: string | null
  repId: string | null
  // The bounced rep's live flag state at fetch time (for the "still flagged" /
  // "updated since" line — the one deliberate current-state read, not attribution).
  repEmailBouncedAt: string | null
  fromEmail: string | null
  // The read-only detail expander content, rendered verbatim (never edited).
  detail: Record<string, unknown>
}

// Dead-letter / feed kind → plain-words label (the failure view's vocabulary).
export const FAILURE_KIND_LABELS: Record<string, string> = {
  no_token: 'Reply to a summary email',
  unknown_token: 'Unknown reply token',
  expired_window: 'Reply after the 30-day window',
  auto_reply: 'Auto-reply',
  malformed: 'Unreadable inbound email',
  bounce: 'Bounced email',
  outbox: 'Email delivery failed',
  // Story 6.1: the sms transport's chip. Same 'outbox' category and same red
  // tone — the transport is named so the chip never claims the wrong channel.
  outbox_sms: 'SMS delivery failed',
  unrecognized_caller: 'Unregistered caller',
  // Story 6.2: three failed code read-backs on one call.
  verification: 'Caller verification locked out',
}

// The chip tone per category (DESIGN.md's named mapping). A text label ALWAYS
// accompanies the hue (never colour alone — the badge-with-label rule).
export function failureChipTone(category: FailureCategory): FailureChipTone {
  switch (category) {
    // 'verification' (Story 6.2) shares the advisory tone with 'bounce':
    // nothing was lost — the caller was refused, which is the control working.
    // The usual cause is a stale phone number on the rep roster, and the fix
    // lives there.
    case 'bounce':
    case 'verification':
      return 'orange' // advisory
    case 'unrecognized_caller':
      return 'gray' // neutral adoption/config signal
    case 'dead_letter':
    case 'outbox':
      return 'red' // critical
  }
}

// The human label for a FailureRow's chip.
export function failureLabel(row: FailureRow): string {
  if (row.category === 'outbox') {
    return row.channel === 'sms'
      ? FAILURE_KIND_LABELS.outbox_sms
      : FAILURE_KIND_LABELS.outbox
  }
  if (row.category === 'unrecognized_caller') {
    return FAILURE_KIND_LABELS.unrecognized_caller
  }
  if (row.category === 'verification') {
    return FAILURE_KIND_LABELS.verification
  }
  return FAILURE_KIND_LABELS[row.kind ?? ''] ?? 'Failure'
}

// The default-filter predicate: auto_reply rows are noise-tier (4.6 Decision 10)
// and EXCLUDED by default; the view's "Include auto-replies" toggle includes them.
export function isDefaultVisible(row: FailureRow): boolean {
  return row.kind !== 'auto_reply'
}

// --- Normalizers (one per source) -------------------------------------------

function deadLetterToRow(dl: DeadLetterRowInput): FailureRow {
  const isBounce = dl.kind === 'bounce'
  const label = FAILURE_KIND_LABELS[dl.kind] ?? 'Dead letter'
  return {
    id: `dl-${dl.id}`,
    source: 'dead_letter',
    category: isBounce ? 'bounce' : 'dead_letter',
    kind: dl.kind,
    channel: null,
    occurredAt: dl.created_at ?? '',
    summary: dl.from_email ? `${label} — ${dl.from_email}` : label,
    organizationId: dl.organization_id,
    ticketId: dl.ticket_id,
    repId: dl.rep_id,
    repEmailBouncedAt: dl.rep?.email_bounced_at ?? null,
    fromEmail: dl.from_email,
    detail: {
      kind: dl.kind,
      from_email: dl.from_email,
      subject: dl.subject,
      ...(dl.rep?.name ? { rep: dl.rep.name } : {}),
      payload: dl.payload,
    },
  }
}

// Story 6.1: an outbox failure is now either transport. Category stays 'outbox'
// (one table filterable by kind — EXPERIENCE.md); only the wording names the
// transport, in the same neutral register as the email copy.
function notificationToRow(n: YakNotification): FailureRow {
  // failuresService filters to the outbox transports (.in('channel',
  // ['email','sms'])), so this narrowing is total in practice. An 'in_app' row
  // reaching here would be an upstream bug — record null rather than silently
  // labelling it "Email delivery failed" (Story 6.1 review).
  const channel: YakOutboxChannel | null =
    n.channel === 'sms' || n.channel === 'email' ? n.channel : null
  const transport = channel === 'sms' ? 'SMS' : 'Email'
  return {
    id: `nt-${n.id}`,
    source: 'notification',
    category: 'outbox',
    kind: null,
    channel,
    occurredAt: n.created_at ?? '',
    summary: `${transport} delivery failed — ${n.type}`,
    organizationId: n.organization_id,
    ticketId: n.ticket_id,
    // Story 6.1 review: sms is rep-only by construction (resolveAndComposeSms
    // terminally rejects every non-rep row) and the dominant terminal Twilio
    // codes — 21211 invalid To, 21610 opted out, 30003 unreachable — are all
    // fixed on the rep record. Without this the Attribution column renders '—'
    // for the one failure category whose remediation IS the rep roster.
    repId: n.recipient_rep_id,
    repEmailBouncedAt: null,
    fromEmail: null,
    detail: {
      type: n.type,
      recipient_kind: n.recipient_kind,
      recipient_rep_id: n.recipient_rep_id,
      attempts: n.attempts,
      last_error: n.last_error,
    },
  }
}

function callToRow(c: YakCall): FailureRow {
  return {
    id: `cl-${c.id}`,
    source: 'call',
    category: 'unrecognized_caller',
    kind: null,
    channel: null,
    occurredAt: c.started_at ?? c.created_at ?? '',
    // Neutral voice (EXPERIENCE.md): an adoption/config signal, never an error.
    summary: c.caller_number
      ? `Call from an unregistered number — ${c.caller_number}`
      : 'Call from an unregistered number',
    organizationId: c.organization_id,
    ticketId: null,
    repId: null,
    repEmailBouncedAt: null,
    fromEmail: null,
    detail: {
      caller_number: c.caller_number,
      disposition: c.disposition,
      started_at: c.started_at,
    },
  }
}

// Story 6.2: a locked-out verification row, in the neutral register EXPERIENCE.md
// asks for — counts and verbs, no alarm. The chip carries the label; this line
// carries who and from where, which is what an admin needs to decide whether the
// roster is wrong or the call was not who it claimed to be.
function verificationToRow(v: VerificationRowInput): FailureRow {
  const who = v.rep?.name ?? 'a rep'
  return {
    id: `cv-${v.id}`,
    source: 'verification',
    category: 'verification',
    kind: null,
    channel: null,
    occurredAt: v.created_at ?? '',
    summary: `Caller verification locked out — ${who}, ${v.caller_number}`,
    organizationId: v.organization_id,
    ticketId: null,
    repId: v.rep_id,
    repEmailBouncedAt: null,
    fromEmail: null,
    detail: {
      caller_number: v.caller_number,
      attempts: v.attempt_count,
      codes_sent: v.send_count,
      ...(v.rep?.name ? { rep: v.rep.name } : {}),
    },
  }
}

// The dead-letter read shape: a yak_dead_letters row plus the optional embedded
// rep (name + live flag state) resolved by the service's composite-FK embed.
export interface DeadLetterRowInput extends YakDeadLetter {
  rep?: { name: string; email_bounced_at: string | null } | null
}

// The verification read shape: a yak_call_verifications row plus the embedded
// rep name (composite-FK embed). No email_bounced_at — a lockout says nothing
// about the rep's email.
export interface VerificationRowInput extends YakCallVerification {
  rep?: { name: string } | null
}

// mergeFailureRows — the pure unifier. Normalizes the four feeds into FailureRow[],
// sorts newest-first (occurredAt desc; id tiebreak for stability). Filtering
// (auto-reply default exclusion, kind filter) and pagination are applied by the
// component over this merged list.
//
// The fourth feed (Story 6.2) defaults to empty so that every pre-6.2 call site
// and test keeps its meaning: adding a feed must not silently change what a
// three-argument call returns.
export function mergeFailureRows(
  deadLetters: DeadLetterRowInput[],
  failedNotifications: YakNotification[],
  calls: YakCall[],
  verifications: VerificationRowInput[] = []
): FailureRow[] {
  const rows: FailureRow[] = [
    ...deadLetters.map(deadLetterToRow),
    ...failedNotifications.map(notificationToRow),
    ...calls.map(callToRow),
    ...verifications.map(verificationToRow),
  ]
  rows.sort((a, b) => {
    if (a.occurredAt !== b.occurredAt) {
      return a.occurredAt < b.occurredAt ? 1 : -1
    }
    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0
  })
  return rows
}
